import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { SharedService, StaffIframe } from '../shared/shared.service';
import { StorageService } from '../storage/storage.service';
import { Page } from '../edit/predefined-templates.service';

@Injectable()
export class RoutingService {
  private _history = [];
  baseUri;
  private _previousUrl = '';
  private _currentUrl = '';
  private _skipUrlHistory = ['/login', '/home', '/page'];

  constructor(
    private _router: Router,
    private _sharedService: SharedService,
    private _storageService: StorageService,
    @Inject(DOCUMENT) private readonly _document,
  ) {
    this.baseUri = _document.location.origin;
  }

  /**
   *set from app.component based on current route. Always contains the current route
   */
  setCurrentUrl(url: string) {
    if (url !== this._currentUrl && !this._isSkipUrl(url)) {
      this._previousUrl = this._currentUrl;
      this._currentUrl = url;
    }
  }

  /**
   * gets current route which is set from app.component
   */
  get currentUrl(): string {
    return this._currentUrl;
  }

  get previousUrl(): string {
    return this._previousUrl;
  }

  clearUrls(): void {
    this._currentUrl = '';
    this._previousUrl = '';
  }

  private _isSkipUrl(url: string): boolean {
    return this._skipUrlHistory.some((skipUrl) => url.includes(skipUrl));
  }

  redirectAfterLogin(userData, isGipLogin: boolean = false, redirectUrl: string = ''): void {
    // GIP login.
    if (isGipLogin && userData?.gipRedirectUrl) {
      const origin = this.baseUri;
      const url = `${origin}${userData.gipRedirectUrl}`;
      window.location.href = url;
    }

    // redirect to url.
    if (redirectUrl) {
      this._router.navigateByUrl(redirectUrl);
      return;
    }

    // kids Cat.
    if (this._sharedService.getIsKidsCat()) {
      this.goToKidsCat();
      return;
    }

    // default landing page.
    const pageToRoute = userData?.opacOptions?.defaultLandingPage;
    switch (pageToRoute) {
      case 'Advanced Search':
        this.goToAdvancedSearch(false);
        break;
      case 'Staff Dashboard':
        this.goToDashboard();
        break;
      case 'Request Manager':
        this.goToRequestManager();
        break;
      case 'Home':
      default:
        this.goToHome();
        break;
    }
  }

  goToPreviousUrl() {
    if (this._previousUrl) {
      this._router.navigateByUrl(this._previousUrl);
    } else {
      this.goToHome();
    }
  }

  redirectOnAutoLogin(queryParams: Params) {
    this._router.navigate([this._document.location.pathname], {
      queryParams: queryParams,
    });
  }

  goToLogin(destinationUrl: string = null) {
    const userData = this._storageService.getUserData();
    const queryParams = {
      cid: userData.customerId,
      lid: userData.libraryId,
    };

    if (destinationUrl) {
      queryParams['redirect'] = destinationUrl; // Route to redirect query parameter
    }

    this._router.navigate(['/login'], { queryParams: queryParams });
  }

  goToHold(agControlId: string): void {
    // TODO: had a discussion with Steve and Garrick and we need to always redirect after login. Possibly goes in AuthGuard
    this._router.navigate(['/hold', agControlId]);
  }

  goToAddItem(agControlId: string | number) {
    this._router.navigate([`/holdings/add/${agControlId}`]);
  }

  goToEditItem(holdingId: string | number) {
    this._router.navigate([`/holdings/edit/${holdingId}`]);
  }

  goToSearch(
    searchId: string,
    isDidYouMeanSearch: boolean = false,
    startRecord: number = 1,
    replaceUrl: boolean = false,
  ): void {
    const queryParams = {
      searchId: searchId,
    };
    if (isDidYouMeanSearch) {
      queryParams['dym'] = true;
    }
    queryParams['startRecord'] = startRecord;
    this.goToSearchWithQueryParams(queryParams, replaceUrl);
  }

  goToSearchWithQueryParams(queryParams, replaceUrl = false) {
    this._router.navigate(['/search'], { queryParams: queryParams, replaceUrl: replaceUrl });
  }

  goToIllRequestEdit(): void {
    this._router.navigate(['/request-edit']);
  }

  goToUrl(url: string): void {
    if (url === this._currentUrl) {
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router.navigateByUrl(url);
      });
    } else {
      this._router.navigateByUrl(url);
    }
  }

  goToUrlWithParams(url: string, queryParams: any) {
    this._router.navigate([url], { queryParams: queryParams });
  }

  goToIllRequest(queryParams: any): void {
    this._router.navigate(['/request'], { queryParams: queryParams });
  }

  goToBlankIllRequest() {
    this._router.navigate(['/request/blank']);
  }

  goToBatchStatusUpdate(): void {
    this._router.navigate(['/bsu']);
  }

  goToShippingLabelConfig(): void {
    this._router.navigate(['/shippingLabels/config']);
  }

  goToEditCustomPage(type): void {
    this._router.navigate(['/home-edit'], { queryParams: { type: type, d: Date.now() } });
  }

  goToCirculationLocationGroups(): void {
    this._router.navigateByUrl('/circulation/locationgroups');
  }

  goToKidsCat(): void {
    this._router.navigateByUrl('/kids');
  }

  goToHome(): void {
    const userData = this._storageService.getUserData();
    this._router.navigate(['/home'], {
      queryParams: { cid: userData.customerId, lid: userData.libraryId },
    });
  }

  goToSignUp(cid: string, lid: string): void {
    this._router.navigate(['/signup/lib'], { queryParams: { cid: cid, lid: lid } });
  }

  goToAdvancedSearch(history: boolean = false, fromSearch: boolean = false) {
    const queryParams = {
      history: history,
    };
    if (fromSearch) {
      queryParams['fromSearch'] = true;
    }
    this._router.navigate(['/advancedsearch'], { queryParams });
  }

  goToPageById(customerCarePage): void {
    if (!customerCarePage) {
      console.error('customer care page not found');
      return;
    }
    this._router.navigate(['/page', customerCarePage._id], {
      queryParams: {
        cid: this._storageService.getCid(),
        lid: this._storageService.getLid(),
        libId: this._storageService.getItem('libId'),
      },
    });
  }

  goToBrief(searchId: string, format: string, agControlId: string): void {
    // agControlId isn't needed on brief page but it's needed to apply a-link:visited styling
    // in order to make the url unique
    this._router.navigate(['/brief'], {
      queryParams: {
        agctrlid: agControlId,
        searchId: searchId,
        format: format,
      },
    });
  }

  goToBriefShareIt(
    // agControlId isn't needed on brief page but it's needed to apply a-link:visited styling
    // in order to make the url unique
    searchId: string,
    format: string,
    agControlId: string,
    resourceAgControlIds: string[] = [],
  ): void {
    const queryParams = {
      agctrlid: agControlId,
      searchId: searchId,
      format: format,
    };
    if (resourceAgControlIds.length) {
      queryParams['resourceAgControlIds'] = resourceAgControlIds;
    }

    this._router.navigate(['/briefShareit'], { queryParams });
  }

  goToFullRecord(
    agControlId: string,
    shard: string,
    searchId: string,
    isRefdb: boolean,
    refdbFullRecUrl: string,
    listViewPageOffset: number = Number.MAX_SAFE_INTEGER,
  ): void {
    if (listViewPageOffset !== Number.MAX_SAFE_INTEGER) {
      this._storageService.setSearchResultsYOffset(listViewPageOffset);
    }

    if (isRefdb) {
      this.goToFullRecordRefDb(refdbFullRecUrl);
      return;
    }

    this.goToFullRecordAgControlId(agControlId, shard, searchId);
  }

  goToFullRecordRelativeUrl(relativeUrl: string): void {
    if (SharedService.isEmpty(relativeUrl)) {
      return;
    }

    this.goToUrl(relativeUrl);
  }

  goToFullRecordByAgControlId(agControlId: string, libraryDbPoolKey: number) {
    this._router.navigate(['/details'], {
      queryParams: { agctrlid: agControlId, libraryDbPoolKey: libraryDbPoolKey },
    });
  }

  goToFullRecordAgControlId(agControlId: string, shard: string, searchId: string): void {
    this._router.navigate(['/details'], {
      queryParams: {
        agctrlid: agControlId,
        shard: shard,
        searchId: searchId,
      },
    });
  }

  goToFullRecordNewTab(agControlId: string, shard: string): void {
    const userData = this._storageService.getUserData();
    const fullRecordUrl =
      this._document.location.origin +
      '/details' +
      '?agctrlid=' +
      agControlId +
      '&shard=' +
      shard +
      '&cid=' +
      userData.customerId +
      '&lid=' +
      userData.libraryId;
    window.open(fullRecordUrl);
  }

  goToFullRecordRefDb(refdbFullRecUrl: string): void {
    if (!SharedService.isEmpty(refdbFullRecUrl)) {
      const staffWindow: StaffIframe = {
        targetUrl: refdbFullRecUrl,
        target: undefined,
        windowFeatures: undefined,
      };
      this._sharedService.setStaffIframeBS(staffWindow);
    } else {
      this._sharedService.openSnackBar('Unable to load Full Record.');
    }
  }

  goToDashboard(): void {
    this._sharedService.setOpenStaffPage(null);
    this._router.navigate(['/dashboard']);
  }

  goToRequestManager(): void {
    this._sharedService.setOpenStaffPage(null);
    this._router.navigate(['/staff/ill/request-manager']);
  }

  goToMarcReportPage(): void {
    this._router.navigate(['/marc/report']);
  }

  /**
   * Navigate to Marc-Edit with SourceKey
   * @param agControlId The AGCN to show
   * @param sourceKey The record location information
   * @param editBibRecordLink The url to open AGCat
   */
  goToMarcEditor(sourceKeys: string | string[], editBibRecordLink: string = null): void {
    const keysArray = Array.isArray(sourceKeys) ? sourceKeys : [sourceKeys];
    const uniqueSourceKeys = [...new Set(keysArray)];
    const encodedKeys = uniqueSourceKeys
      .filter((key) => key != null && key.trim() !== '')
      .map((key) => encodeURIComponent(key.trim()));

    this._router.navigate(['/marc/edit'], {
      queryParams: {
        ...(encodedKeys.length && { sourceKeys: encodedKeys.join(',') }),
        editBibRecordLink: editBibRecordLink,
      },
    });
  }

  goToAccountProfile(returnUrl: string = ''): void {
    const queryParams = { returnUrl: returnUrl };

    this._router.navigate(['/account/profile'], { queryParams: queryParams });
  }

  goToAccountPreferences(): void {
    this._router.navigateByUrl('/account/preferences');
  }

  goToAccountResources(): void {
    this._router.navigateByUrl('/account/resources');
  }

  goToAccountWebLinks(): void {
    this._router.navigateByUrl('/account/webLinks');
  }

  goToItemsOut(): void {
    this._router.navigateByUrl('/items/itemsOut');
  }

  goToItemsHold(): void {
    this._router.navigateByUrl('/items/itemsHold');
  }

  goToLostItems(): void {
    this._router.navigateByUrl('/items/lostItems');
  }

  goToCheckoutHistory(): void {
    this._router.navigateByUrl('/items/checkoutHistory');
  }

  goToSearchAlerts(): void {
    this._router.navigateByUrl('/search/alerts');
  }

  goToFines(): void {
    this._router.navigateByUrl('/items/itemPayment');
  }

  goToOtherLibraries(): void {
    this._router.navigateByUrl('/items/otherLibraries');
  }

  goToYourList(): void {
    this._router.navigateByUrl('/list');
  }

  goToPage(page: Page) {
    if (page.linkPage) {
      window.open(page.redirectUrl, '_blank');
    } else {
      this._router.navigate(['/page', page._id], {
        queryParams: { libId: this._storageService.getItem('libId') },
      });
    }
  }

  goToEditUser(userId: number) {
    this._router.navigate(['/staff/users/', userId]);
  }

  pushOnBackStack(route: string = null) {
    if (route) {
      this._history.push(route);
    } else {
      this._history.push(this._router.url);
    }
  }

  // used in conjunction with pushOnBackStack
  goBack() {
    const url = this._history.pop();
    if (url) {
      this._router.navigateByUrl(url);
    }
  }

  hasBackRoute(): boolean {
    return this._history.length != 0;
  }

  getCurrentRouteTitle() {
    const url = this._router.url;

    if (url === '/' || url.includes('/home') || url.includes('/page/')) {
      return 'Home';
    } else if (url.includes('/dashboard')) {
      return 'Staff Dashboard';
    } else if (url.includes('/request/search')) {
      if (url.includes('borrower')) {
        return 'Borrower Requests';
      } else if (url.includes('lender')) {
        return 'Lender Requests';
      } else {
        return 'Search by Request Number';
      }
    } else if (url.includes('/search') || url.includes('/brief')) {
      return 'Search Result';
    } else if (url.includes('/details') || url.includes('/record')) {
      return 'Full Record';
    } else if (url.includes('/hold/')) {
      return 'Place Hold';
    } else if (url.includes('/request') && !url.includes('/request-edit')) {
      return 'Ill Request';
    } else if (url.includes('/advancedsearch')) {
      return 'Advanced Search';
    } else if (url.includes('/list')) {
      return url.includes('listit') ? 'LISTit' : 'Your List';
    } else if (url.includes('/items/')) {
      return 'Your Items';
    } else if (url.includes('/account/')) {
      return 'Your Settings';
    } else if (url.includes('/login')) {
      return 'Login';
    } else if (url.includes('/marc/report')) {
      return 'MARC Tag Report';
    } else if (url.includes('/marc/edit')) {
      return 'AGEdit';
    } else if (url.includes('/marc/definition')) {
      return 'MARC Definition';
    } else if (url.includes('/request-edit')) {
      return 'Configure Request Form';
    } else if (url.includes('/staff/catalog')) {
      return 'Catalog Maintenance';
    } else if (url.includes('/staff/users')) {
      if (url.includes('/new')) {
        return 'New User';
      } else if (/\d+/.test(url)) {
        return 'Edit User';
      }
      return 'Manage Users';
    } else if (url.includes('/bsu/bdu')) {
      return 'Circ Admin > Batch Update Due Dates';
    }
    try {
      const urlParts = url.split('/');
      if (urlParts.length > 1) {
        let pathname = urlParts[1];
        const queryIndex = pathname.indexOf('?');
        if (queryIndex > -1) {
          pathname = pathname.substring(0, queryIndex);
        }
        return pathname.charAt(0).toUpperCase() + pathname.substring(1).toLowerCase() + ' Page';
      }
    } catch (e) {
      return 'Site Page';
    }
  }
}
